import React, { FC, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { noteCategories, NoteCategory, UserNoteCreate } from '../../../../../shared/user/notes';
import { PageHeader, PermissionBoundary, UserStateComponent } from '../../../../components';
import { AddNoteForm } from '../../../../modules/notes/addNoteForm';
import { UserNoteItem } from '../../../../modules/notes/noteItem';
import { useTranslation } from '../../../../translations';
import { capitalize, Fetcher, useFetcher, useForm, useInputState } from '../../../../utils';
import { useDebounceValue } from '../../../../utils/debounce';

export const UserNotescomponent: FC = () => {
  const { ts } = useTranslation();
  const [search, setSearch] = useInputState('');
  const [category, setCategory] = useInputState<NoteCategory | 'all'>('all');
  const [createModal, setCreateModal] = useState(false);

  const searchDebounce = useDebounceValue<string>(search, 500);

  const fetcher = useFetcher(async () => {
    return await api.getNotes({
      category: category === 'all' ? undefined : category,
      search: searchDebounce === '' ? undefined : searchDebounce,
    });
  }, [category, searchDebounce]);

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <UserStateComponent>
      <PageHeader>{ts('user_notes')}</PageHeader>
      {createModal && (
        <CreateNoteModal
          close={() => {
            setCreateModal(false);
            fetcher.refresh();
          }}
        />
      )}
      <Row>
        <Col>
          <FormGroup>
            <Input
              autoFocus
              id="searchNotes"
              onChange={setSearch}
              placeholder={ts('search_notes')}
              type="search"
              value={search}
            />
          </FormGroup>
        </Col>
        <Col md={6} xs={12}>
          <Input
            className="mb-10"
            id="category"
            name="category"
            onChange={setCategory}
            style={{ width: 'auto', display: 'inline' }}
            type="select"
            value={category}
          >
            <option value="all">({ts('all')})</option>
            {noteCategories.map((cat) => (
              <option key={cat} value={cat}>
                {capitalize(cat)}
              </option>
            ))}
          </Input>
        </Col>
        <PermissionBoundary inline requiredPermissions="user:note:create">
          <Col md="auto">
            <Button
              color="primary"
              id="addNoteModal"
              onClick={() => {
                setCreateModal(true);
              }}
            >
              {ts('create.note')}
            </Button>
          </Col>
        </PermissionBoundary>
      </Row>

      {fetcher.data.map((note) => {
        return <UserNoteItem key={note.id} note={note} refresh={fetcher.refresh} />;
      })}
    </UserStateComponent>
  );
};

interface CreateNoteModalProps {
  readonly close: () => void;
}

const CreateNoteModal: FC<CreateNoteModalProps> = ({ close }) => {
  const { ts } = useTranslation();
  const [note, setNote] = useState<UserNoteCreate>({
    category: 'misc',
    text: '',
  });

  const form = useForm(async () => {
    if (!note.userId && note.category !== 'ban') {
      toast.error(ts('user_must_be_selected'));
      return;
    }

    await api.createUserNote(note);
    toast.success(ts('note_has_been_added'));
    close();
  }, [close, note, ts]);

  return (
    <Modal className="modal-large" id="createNoteModal" isOpen toggle={close}>
      <form onSubmit={form.onSubmit}>
        <ModalHeader>{ts('note.create_new_user_note')}</ModalHeader>
        <ModalBody>
          <AddNoteForm note={note} setNote={setNote} />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" disabled={form.saving} id="complete" type="submit">
            {ts('common.create')}
          </Button>{' '}
          <Button color="secondary" id="cancel" onClick={close} outline>
            {ts('common.cancel')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
